import { gql } from '@apollo/client';
import {
  AdminTicketOrderRowFields,
  TicketAffiliateLinkFields,
  TicketEventDetailsFields,
  TicketEventListFields,
  TicketEventOrderRowFields,
  TicketingSettingFields,
  TicketMatchupFields,
  TicketPresaleCodeFields,
  TicketTierFields,
  TicketVenueFields,
} from './fragments';

export const GET_TICKET_EVENTS = gql`
  query GetTicketEvents($input: HostedTicketEventsInput!) {
    hostedTicketEvents(input: $input) {
      entities {
        ...TicketEventListFields
      }
      total
      offset
      limit
    }
  }
  ${TicketEventListFields}
`;

export const GET_TICKET_EVENT = gql`
  query GetTicketEvent($input: HostedTicketEventInput!) {
    hostedTicketEvent(input: $input) {
      ...TicketEventDetailsFields
    }
    hostedTicketTiers(input: $input) {
      ...TicketTierFields
    }
  }
  ${TicketEventDetailsFields}
  ${TicketTierFields}
`;

export const GET_TICKET_EVENT_SALES_REPORT = gql`
  query GetTicketEventSalesReport($input: HostedTicketEventInput!) {
    ticketEventSalesReport(input: $input) {
      totals {
        grossRevenue
        ticketRevenue
        confirmedOrders
        ticketsSold
        refundedOrders
        refundedRevenue
        inReviewOrders
        inReviewRevenue
        compTickets
        checkedIn
        maxAdmissions
      }
      byTier {
        tierId
        tierLabel
        price
        capacity
        available
        ticketsSold
        revenue
        compTickets
        checkedIn
      }
      byDay {
        day
        orders
        ticketsSold
        revenue
      }
      byPresaleCode {
        codeId
        code
        label
        orders
        ticketsSold
        revenue
      }
      checkout {
        reservedBuyers
        reservations
        abandonedReservations
        checkouts
        paidOrders
        declinedOrders
        timedOutOrders
        pendingOrders
        payingBuyers
      }
    }
  }
`;

export const GET_TICKET_EVENT_ORDERS = gql`
  query GetTicketEventOrders($input: TicketEventOrdersInput!) {
    ticketEventOrders(input: $input) {
      entities {
        ...TicketEventOrderRowFields
      }
      total
      offset
      limit
    }
  }
  ${TicketEventOrderRowFields}
`;

export const GET_TICKETING_LAUNCHED = gql`
  query GetTicketingLaunched {
    ticketingLaunched
  }
`;

export const GET_TICKETING_SETTING = gql`
  query GetTicketingSetting {
    getTicketingSetting {
      ...TicketingSettingFields
    }
  }
  ${TicketingSettingFields}
`;

export const GET_TICKET_EVENT_FORM_DEFAULTS = gql`
  query GetTicketEventFormDefaults {
    getTicketingSetting {
      ...TicketingSettingFields
    }
    ticketEventScheduleDefaults {
      eventDurationSeconds
      salesCloseAfterStartSeconds
      maxSalesCloseAfterStartSeconds
    }
  }
  ${TicketingSettingFields}
`;

export const GET_TICKET_VENUES = gql`
  query GetTicketVenues($input: MyTicketVenuesInput!) {
    myTicketVenues(input: $input) {
      entities {
        ...TicketVenueFields
      }
      total
      offset
      limit
    }
  }
  ${TicketVenueFields}
`;

export const GET_TICKET_VENUE_PLACE_PREDICTIONS = gql`
  query GetTicketVenuePlacePredictions(
    $input: TicketVenuePlacePredictionsInput!
  ) {
    ticketVenuePlacePredictions(input: $input) {
      entities {
        id
        text
      }
    }
  }
`;

export const GET_TICKET_VENUE_PLACE_DETAILS = gql`
  query GetTicketVenuePlaceDetails($input: GetPlaceDetailsInput!) {
    ticketVenuePlaceDetails(input: $input) {
      id
      name
      formattedAddress
      addressLine1
      city
      state
      stateCode
      postalCode
      country
      countryCode
      timezone
      location {
        latitude
        longitude
      }
    }
  }
`;

export const GET_TICKET_EVENT_HOSTS = gql`
  query GetTicketEventHosts(
    $storeRoles: [UserRole!]
    $input: AdminStoresFilterInput!
  ) {
    adminStores(storeRoles: $storeRoles, input: $input) {
      entities {
        id
        role
        firstName
        lastName
        email
        storeDetails {
          id
          storeName
          avatarURL
        }
      }
      total
    }
  }
`;

export const GET_TICKET_EVENT_HOST = gql`
  query GetTicketEventHost($input: StoreGetInput!) {
    store(input: $input) {
      id
      firstName
      lastName
      storeDetails {
        id
        storeName
      }
    }
  }
`;

export const SEARCH_TICKET_USERS = gql`
  query SearchTicketUsers($input: UsersFilterInput) {
    users(input: $input) {
      entities {
        id
        firstName
        lastName
        email
      }
      total
    }
  }
`;

export const GET_TICKET_PRESALE_CODES = gql`
  query GetTicketPresaleCodes($input: HostedTicketEventInput!) {
    ticketPresaleCodes(input: $input) {
      ...TicketPresaleCodeFields
    }
  }
  ${TicketPresaleCodeFields}
`;

export const GET_TICKET_SCAN_HISTORY = gql`
  query GetTicketScanHistory($input: TicketScanHistoryInput!) {
    ticketScanHistory(input: $input) {
      entities {
        id
        result
        message
        gate
        scannedAt
        ticket {
          serial
          holderName
          tierLabel
          status
        }
      }
      total
      offset
      limit
    }
  }
`;

export const GET_TICKET_EVENT_PEOPLE = gql`
  query GetTicketEventPeople(
    $input: HostedTicketEventInput!
    $participantsInput: StreamParticipantRowsInput!
  ) {
    ticketEventAnalytics(input: $input) {
      participantsCount
      interviewedCount
      participants {
        id
        firstName
        lastName
        slug
        storeDetails {
          id
          storeName
        }
      }
      interviews {
        id
        status
        interviewee {
          id
        }
        stream {
          id
          slug
          store {
            id
            slug
          }
        }
      }
    }
    streamParticipantRows(input: $participantsInput) {
      entities {
        id
        userId
        name
        username
      }
      total
      offset
      limit
    }
  }
`;

export const GET_TICKET_EVENT_PARTICIPANT_INVITE = gql`
  query GetTicketEventParticipantInvite($input: HostedTicketEventInput!) {
    getOrCreateTicketEventParticipantInvite(input: $input) {
      invite
    }
  }
`;

export const GET_TICKET_EVENT_AFFILIATES = gql`
  query GetTicketEventAffiliates($input: StreamAffiliateLinksInput!) {
    getStreamAffiliateLinks(input: $input) {
      entities {
        link {
          ...TicketAffiliateLinkFields
          user {
            id
            firstName
            lastName
            email
            storeDetails {
              id
              storeName
            }
          }
        }
        salesCount
        ticketsSoldCount
        revenue
        commissionEarned
        isParticipant
      }
      total
      offset
      limit
    }
  }
  ${TicketAffiliateLinkFields}
`;

export const GET_TICKET_EVENT_AFFILIATE_SUMMARY = gql`
  query GetTicketEventAffiliateSummary($input: StreamAffiliateStreamInput!) {
    getStreamAffiliateSummary(input: $input) {
      affiliateTicketsSoldCount
      affiliateCommissionTotal
    }
  }
`;

export const GET_TICKET_EVENT_MATCHUPS = gql`
  query GetTicketEventMatchups($input: HostedTicketEventInput!) {
    hostedTicketEvent(input: $input) {
      id
      fanPollingEnabled
      matchups {
        ...TicketMatchupFields
      }
    }
  }
  ${TicketMatchupFields}
`;

export const GET_TICKET_EVENT_VOTE_RESULTS = gql`
  query GetTicketEventVoteResults($ticketEventId: String!) {
    getTicketEventVoteResults(ticketEventId: $ticketEventId) {
      matchupId
      total
      competitorVotes {
        competitorId
        votes
      }
    }
  }
`;

export const GET_TICKET_EVENT_HOST_STREAMS = gql`
  query GetTicketEventHostStreams($input: StreamFilterInput) {
    adminStreams(input: $input) {
      entities {
        id
        name
        scheduleDate
        ticketEvent {
          id
          status
        }
      }
    }
  }
`;

export const GET_STREAM_PARTICIPANT_INVITE = gql`
  query GetTicketEventStreamInvite($input: StreamParticipantInviteInputAdmin!) {
    getOrCreateStreamParticipantInviteAdmin(input: $input) {
      id
      invite
    }
  }
`;

export const GET_TICKET_ORDER_HISTORY = gql`
  query GetTicketOrderHistory($input: TicketOrderHistoryInput!) {
    ticketOrderHistory(input: $input) {
      id
      fromStatus
      toStatus
      actor {
        id
        name
      }
      reason
      createdAt
    }
  }
`;

export const GET_ADMIN_TICKET_ORDERS = gql`
  query GetAdminTicketOrders($input: AdminTicketOrdersInput!) {
    adminTicketOrders(input: $input) {
      entities {
        ...AdminTicketOrderRowFields
      }
      total
      offset
      limit
    }
  }
  ${AdminTicketOrderRowFields}
`;

export const GET_TICKET_EVENT_SALES_SUMMARIES = gql`
  query GetTicketEventSalesSummaries($input: TicketEventSalesSummariesInput!) {
    ticketEventSalesSummaries(input: $input) {
      ticketEventId
      ticketsSold
      ticketRevenue
      grossRevenue
      refundedOrders
      compTickets
      checkedIn
      maxAdmissions
    }
  }
`;

export const GET_TICKETING_OVERVIEW = gql`
  query GetTicketingOverview($input: TicketingOverviewInput) {
    ticketingOverview(input: $input) {
      grossRevenue
      ticketRevenue
      serviceFees
      confirmedOrders
      ticketsSold
      refundedOrders
      refundedRevenue
      pendingRefunds
      failedRefunds
      inReviewOrders
      compTickets
      checkedIn
      eventsOnSale
    }
  }
`;
